import AbstractCustomerValueModel from '../abstract_customer_value_model';

class Cadency extends AbstractCustomerValueModel {
  // IMPACT VARIABLES ==================

  percent_reduction_in_time_on_financial_transaction_matching() {
    return this.input.percent_reduction_in_time_on_financial_transaction_matching / 100;
  }

  percent_reduction_in_the_number_of_accounts_to_be_reconciled() {
    return this.input.percent_reduction_in_the_number_of_accounts_to_be_reconciled / 100;
  }

  percent_reduction_in_time_on_journal_entries() {
    return this.input.percent_reduction_in_time_on_journal_entries / 100;
  }

  percent_reduction_in_time_on_a_close() {
    return this.input.percent_reduction_in_time_on_a_close / 100;
  }

  percent_reduction_in_time_on_controls_documentation() {
    return this.input.percent_reduction_in_time_on_controls_documentation / 100;
  }

  percent_reduction_in_time_supporting_external_audit() {
    return this.input.percent_reduction_in_time_supporting_external_audit / 100;
  }

  percent_reduction_in_write_offs() {
    return this.input.percent_reduction_in_write_offs / 100;
  }

  // USER INPUTS =======================

  // L23
  monthly_fte_hours_spent_matching_financial_transactions() {
    return this.input.monthly_fte_hours_spent_matching_financial_transactions;
  }

  // L30
  accounts_to_reconcile_per_month() {
    return this.input.accounts_to_reconcile_per_month;
  }

  // L39
  journal_entries_per_month() {
    return this.input.journal_entries_per_month;
  }

  // L48
  close_tasks_per_month() {
    return this.input.close_tasks_per_month;
  }

  // L53
  annual_fte_hours_spent_on_controls_documentation() {
    return this.input.annual_fte_hours_spent_on_controls_documentation;
  }

  // L13
  employees_supporting_external_audit_annually() {
    return this.input.employees_supporting_external_audit_annually;
  }

  // L12
  accounting_employees() {
    return this.input.accounting_employees;
  }

  // L10
  monthly_transactions() {
    return this.input.monthly_transactions;
  }

  // Configurator - I36
  monthly_price_per_user() {
    return this.input.monthly_price_per_user * this.currency_conversion();
  }

  // Configurator - I38
  price_per_transaction() {
    return this.input.price_per_transaction * this.currency_conversion();
  }

  // Configurator - I39
  monthly_storage_fee() {
    return this.input.monthly_storage_fee * this.currency_conversion();
  }

  // Configurator - I48
  initial_implementation_fee() {
    return this.input.initial_implementation_fee * this.currency_conversion();
  }

  // Configurator - I80
  percentage_annual_price_increase() {
    return this.input.percentage_annual_price_increase / 100;
  }


  // DERIVED INPUTS ====================

  // N72
  reconciler_hourly_salary_fully_loaded() {
    return this.annual_salary_to_fully_loaded_hourly(60000);
  }

  // N80
  journal_entry_preparer_hourly_salary_fully_loaded() {
    return this.annual_salary_to_fully_loaded_hourly(60000);
  }

  // N84
  journal_entry_reviewer_hourly_salary_fully_loaded() {
    return this.annual_salary_to_fully_loaded_hourly(85000);
  }

  // N82
  close_task_accountant_hourly_salary_fully_loaded() {
    return this.annual_salary_to_fully_loaded_hourly(50000);
  }

  // N86
  compliance_accountant_hourly_salary_fully_loaded() {
    return this.annual_salary_to_fully_loaded_hourly(60000);
  }

  annual_salary_to_fully_loaded_hourly(salary) {
    return (salary * this.currency_conversion() * 1.35) / 8 / 260;
  }

  // STATIC INPUTS =====================

  // L31
  monthly_minutes_spent_per_reconciling_account() {
    return 20;
  }

  // L40
  preparer_monthly_minutes_spent_per_journal_entry() {
    return 15;
  }

  // L41
  reviewer_monthly_minutes_spent_per_journal_entry() {
    return 10;
  }

  // L46
  accountants_involved_in_a_close() {
    return 320;
  }

  // L47
  monthly_hours_spent_preparing_for_a_close_per_accountant() {
    return 8;
  }

  // L49
  monthly_minutes_spent_completing_each_close_task() {
    return 15;
  }

  // L14
  hours_per_employee_supporting_external_audit_annually() {
    return 40;
  }

  // L19
  annual_value_of_write_offs_related_to_reconciliations() {
    return 2000000 * this.currency_conversion();
  }

  // VALUE DRIVER LABELS ===============

  increase_operational_efficiencies_label() {
    return this.input.increase_operational_efficiencies_label;
  }

  improve_visibility_and_mitigate_risk_label() {
    return this.input.improve_visibility_and_mitigate_risk_label;
  }

  // VALUE DRIVER CALCULATIONS =========

  all_benefits() {
    return [
      'reduce_time_on_financial_transaction_matching',
      'reduce_the_number_of_accounts_to_be_reconciled',
      'reduce_time_on_journal_entries',
      'reduce_time_on_close_tasks',
      'reduce_time_to_document_control_testing',
      'reduce_time_to_support_external_audit',
      'reduce_write_offs'
    ];
  }

  value_drivers() {
    return ['increase_operational_efficiencies', 'improve_visibility_and_mitigate_risk'];
  }

  increase_operational_efficiencies() {
    return this.reduce_time_on_financial_transaction_matching()
      + this.reduce_the_number_of_accounts_to_be_reconciled()
      + this.reduce_time_on_journal_entries()
      + this.reduce_time_on_close_tasks()
      + this.reduce_time_to_document_control_testing();
  }

  // Note this is overridden in cumulative_value_for_benefit to deal with annual_salary_growth being applied to
  // all value drivers except reduce_write_offs
  improve_visibility_and_mitigate_risk() {
    return this.reduce_time_to_support_external_audit()
      + this.reduce_write_offs();
  }

  reduce_time_on_financial_transaction_matching() {
    return this.monthly_fte_hours_spent_matching_financial_transactions() * this.reconciler_hourly_salary_fully_loaded() * 12
      * this.percent_reduction_in_time_on_financial_transaction_matching();
  }

  reduce_the_number_of_accounts_to_be_reconciled() {
    return ((this.accounts_to_reconcile_per_month() * this.monthly_minutes_spent_per_reconciling_account()) / 60) * 12
      * this.reconciler_hourly_salary_fully_loaded() * this.percent_reduction_in_the_number_of_accounts_to_be_reconciled();
  }

  reduce_time_on_journal_entries() {
    return this.reduce_preparer_time_on_journal_entries() + this.reduce_reviewer_time_on_journal_entries();
  }

  reduce_preparer_time_on_journal_entries() {
    return ((this.journal_entries_per_month() * this.preparer_monthly_minutes_spent_per_journal_entry()) / 60) * 12
      * this.journal_entry_preparer_hourly_salary_fully_loaded() * this.percent_reduction_in_time_on_journal_entries();
  }

  reduce_reviewer_time_on_journal_entries() {
    return ((this.journal_entries_per_month() * this.reviewer_monthly_minutes_spent_per_journal_entry()) / 60) * 12
      * this.journal_entry_reviewer_hourly_salary_fully_loaded() * this.percent_reduction_in_time_on_journal_entries();
  }

  reduce_time_on_close_tasks() {
    return this.reduce_time_preparing_for_a_close() + this.reduce_time_completing_a_close();
  }

  reduce_time_preparing_for_a_close() {
    return this.accountants_involved_in_a_close() * this.monthly_hours_spent_preparing_for_a_close_per_accountant() * 12
      * this.close_task_accountant_hourly_salary_fully_loaded() * this.percent_reduction_in_time_on_a_close();
  }

  reduce_time_completing_a_close() {
    return ((this.close_tasks_per_month() * this.monthly_minutes_spent_completing_each_close_task()) / 60) * 12
      * this.close_task_accountant_hourly_salary_fully_loaded() * this.percent_reduction_in_time_on_a_close();
  }

  reduce_time_to_document_control_testing() {
    return this.annual_fte_hours_spent_on_controls_documentation() * this.compliance_accountant_hourly_salary_fully_loaded()
      * this.percent_reduction_in_time_on_controls_documentation();
  }

  reduce_time_to_support_external_audit() {
    return this.employees_supporting_external_audit_annually() * this.hours_per_employee_supporting_external_audit_annually()
      * this.compliance_accountant_hourly_salary_fully_loaded() * this.percent_reduction_in_time_supporting_external_audit();
  }

  reduce_write_offs() {
    return this.annual_value_of_write_offs_related_to_reconciliations() * this.percent_reduction_in_write_offs();
  }

  // BENEFIT CALCULATIONS ==============

  adoption_rate(year) {
    switch (year) {
      case 1: return 0.6;
      case 2: return 0.8;
      default: return 1.0;
    }
  }

  annual_salary_growth(year) {
    if (year === 1) { return 1; } return Math.pow(1.03, (year - 1));
  }

  // override parent, to include annual salary growth
  cumulative_value_for_benefit(benefit, ending_year) {
    const years = _.range(1, (ending_year == null ? this.year_range_end() : ending_year) + 1);
    return (years.map((year) => {
      if (benefit === 'reduce_write_offs') {
        return this.adoption_rate(year) * this[benefit]();
      } if (benefit === 'improve_visibility_and_mitigate_risk') {
        return (this.adoption_rate(year) * this.reduce_time_to_support_external_audit() * this.annual_salary_growth(year))
          + (this.adoption_rate(year) * this.reduce_write_offs());
      }
      return this.adoption_rate(year) * this[benefit]() * this.annual_salary_growth(year);
    })).reduce(((x, y) => x + y), 0);
  }

  // override parent, to include annual salary growth
  total_potential_benefits_annually(year) {
    let total = 0;

    for (const driver of Array.from(this.all_benefits())) {
      total += driver === 'reduce_write_offs' ? this[driver](year) : this[driver](year) * this.annual_salary_growth(year);
    }

    return total;
  }


  // INVESTMENT CALCULATIONS ===========

  total_annual_investment(year) {
    const initial_setup_fee = 25000 * this.currency_conversion();
    const additional_annual_fees = 2000 * this.currency_conversion();
    const annual_storage_fee = this.monthly_storage_fee() * 12;

    if (year === 1) {
      const initial_implementation_fee = this.initial_implementation_fee();
      return this.annual_subscription_fee(year) + initial_implementation_fee + initial_setup_fee + additional_annual_fees
        + annual_storage_fee;
    }
    return this.annual_subscription_fee(year) + additional_annual_fees + annual_storage_fee;
  }

  annual_subscription_fee(year) {
    const monthly_price_per_user = this.monthly_price_per_user();
    const price_per_transaction = this.price_per_transaction();
    const first_year_fees = (this.accounting_employees() * monthly_price_per_user * 12)
      + (this.monthly_transactions() * 12 * price_per_transaction);
    const annual_price_increase = 1 + this.percentage_annual_price_increase();
    const compound_price_increase = year === 1 ? 1 : Math.pow(annual_price_increase, (year - 1));
    return first_year_fees * compound_price_increase;
  }
}

export default Cadency;
